import React from "react";
import get from "lodash/get";
import findIndex from "lodash/findIndex";
import Select, { Creatable, defaultMenuRenderer } from "react-select";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import config from "../../config";

import connectToStores from "alt-utils/lib/connectToStores";
import UserStore from "../../stores/UserStore";
import PassBucketActions from "../../actions/PassBucketActions";
import PassBucketStore from "../../stores/PassBucketStore";
import EventActions from "../../actions/EventActions";
import EventStore from "../../stores/EventStore";
import VenueActions from "../../actions/VenueActions";
import VenueStore from "../../stores/VenueStore";
import TagActions from "../../actions/TagActions";
import TagStore from "../../stores/TagStore";
import CouponActions from "../../actions/CouponActions";
import CouponStore from "../../stores/CouponStore";
import ItemBucketActions from "../../actions/ItemBucketActions";
import ItemBucketStore from "../../stores/ItemBucketStore";
import FilmActions from "../../actions/FilmActions";
import FilmStore from "../../stores/FilmStore";
import PassActions from "../../actions/PassActions";
import PassStore from "../../stores/PassStore";
import EventBucketActions from "../../actions/EventBucketActions";
import EventBucketStore from "../../stores/EventBucketStore";
import SystemStatesStore from "../../stores/SystemStatesStore";
import InlineButton from "./inline-button.jsx";

class InjectedSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      objects: {},
      fetchedEventBuckets: [],
      isReordering: false,
      reorderingHeight: null,
      hasMounted: false
    };

    this.renderValue = this.renderValue.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.onValueChange = this.onValueChange.bind(this);
  }

  static getStores() {
    return [
      PassBucketStore,
      EventStore,
      VenueStore,
      TagStore,
      CouponStore,
      ItemBucketStore,
      FilmStore,
      PassStore,
      EventBucketStore,
      SystemStatesStore
    ];
  }

  static getPropsFromStores() {
    return {
      getPassBucketsForEventBucket:
        PassBucketStore.getPassBucketsForEventBucket,
      getAllPassBuckets: PassBucketStore.getAllPassBuckets,
      getEventsForEventBucket: EventStore.getEventsForEventBucket,
      getVenuesForEventBucket: VenueStore.getVenuesForEventBucket,
      getTagsForEventBucket: TagStore.getTagsForEventBucket,
      getCouponsForEventBucket: CouponStore.getCouponsForEventBucket,
      getItemBucketsForEventBucket:
        ItemBucketStore.getItemBucketsForEventBucket,
      getFilmsForEventBucket: FilmStore.getFilmsForEventBucket,
      getPassesForEventBucket: PassStore.getPassesForEventBucket,
      getEventBuckets: EventBucketStore.getEventBuckets,
      isFetching:
        PassBucketStore.getState().isFetching ||
        EventStore.getState().isFetching ||
        VenueStore.getState().isFetching ||
        TagStore.getState().isFetching ||
        CouponStore.getState().isFetching ||
        ItemBucketStore.getState().isFetching ||
        FilmStore.getState().isFetching ||
        PassStore.getState().isFetching ||
        EventBucketStore.getState().isFetching
    };
  }

  renderValue = props => {
    const { children, value: option, values } = props;
    const index = findIndex(values, option);
    return (
      <Draggable
        draggableId={option.value}
        type={"select-option"}
        index={index}
      >
        {provided => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...provided.draggableProps.style,
              position: "static",
              padding: 2.5
            }}
          >
            <div className="Select-value">
              <span
                className="Select-value-icon"
                aria-hidden="true"
                onClick={this.onRemove(values, index)}
              >
                ×
              </span>
              <span className="Select-value-label">{children}</span>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  onRemove = (values, index) => () => {
    values.splice(index, 1);
    this.onValueChange(values);
  };

  onValueChange = selected => {
    const { multi, changeValueFunction, onChange } = this.props;
    // Custom changeValue processor (e.g. in order to get the entire object instead of just the ID)
    if (changeValueFunction) {
      if (multi) {
        onChange(
          selected ? selected.map(s => changeValueFunction(s.rawValue)) : []
        );
      } else {
        onChange(selected ? changeValueFunction(selected.rawValue) : null);
      }

      // Select multi
    } else if (multi) {
      onChange(selected ? selected.map(s => s && s.value) : []);

      // Single item
    } else {
      onChange(selected ? selected.value : null);
    }
  };

  onDragStart = () => {
    this.setState({ reorderingHeight: this.select.control.offsetHeight });
  };

  onDragEnd = dropResult => {
    this.setState({ reorderingHeight: null });
    const { destination, source } = dropResult;
    if (!destination || !source) {
      return;
    }
    const destinationIndex = destination.index;
    const sourceIndex = source.index;
    const { value } = this.props;
    const selected = !Array.isArray(value) ? [value] : value;
    const temp = selected[sourceIndex];
    selected.splice(sourceIndex, 1);
    selected.splice(destinationIndex, 0, temp);
    const options = this.getOptions();
    this.onValueChange(
      selected
        .map(id => options.find(option => option.value === id))
        .filter(x => !!x)
    );
  };

  getOptions = () => {
    const {
        labelFormatter,
        isDisabledFunction,
        filterBy,
        injectOptions,
        labelToFilterString
      } = this.props,
      { objects } = this.state;

    return [].concat.apply(
      [],
      Object.keys(objects).map(type => {
        let formatter =
          typeof labelFormatter === "function"
            ? labelFormatter
            : labelFormatter[type];

        return (injectOptions || []).concat(
          objects[type]
            .filter(o => (filterBy ? filterBy(o) : true))
            .map(o => {
              let disabled = isDisabledFunction && isDisabledFunction(o);
              return {
                label: formatter(o, disabled),
                disabled: disabled,
                value: o.id,
                rawValue: o,
                filterableLabel: labelToFilterString
                  ? (labelToFilterString(o) || "")
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .toLowerCase()
                  : undefined
              };
            })
        );
      })
    );
  };

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(newProps) {
    let { types, type, eventBucketId, skipFetch, excludeOlder } = this.props;
    if (typeof type !== "undefined") {
      types = (types || []).concat([type]);
    }
    if (!eventBucketId) {
      if (types.indexOf("event_buckets") !== -1) {
        eventBucketId = [EventBucketStore.getEventBuckets()[0].id];
      } else {
        return;
      }
    }

    const load = eventBucketId => {
      let needsFetch =
        this.state.fetchedEventBuckets.indexOf(eventBucketId) === -1;
      if (!skipFetch && needsFetch) {
        if (types.indexOf("venues") !== -1) {
          VenueActions.fetchAllForBucket.defer(eventBucketId);
        }
        if (types.indexOf("pass_buckets") !== -1) {
          PassBucketActions.fetchAllForEventBucket.defer(eventBucketId);
        }
        if (types.indexOf("all_pass_buckets") !== -1) {
          PassBucketActions.fetchAll.defer();
        }
        if (
          types.indexOf("events") !== -1 ||
          types.indexOf("ticket_buckets") !== -1
        ) {
          const excludeOlderEvents = excludeOlder ? true : false;
          EventActions.fetchAllForBucket.defer(
            eventBucketId,
            false,
            excludeOlderEvents
          );
        }
        if (types.indexOf("tags") !== -1) {
          TagActions.fetchAllForBucket.defer(eventBucketId);
        }
        if (types.indexOf("visibleTags") !== -1) {
          TagActions.fetchAllForBucket.defer(eventBucketId);
        }
        if (types.indexOf("coupons") !== -1) {
          CouponActions.fetchAllForBucket.defer(eventBucketId);
        }
        if (
          types.indexOf("item_buckets") !== -1 ||
          types.indexOf("item_bucket.variants") !== -1
        ) {
          ItemBucketActions.fetchAllForEventBucket.defer(eventBucketId);
        }
        if (types.indexOf("films") !== -1) {
          FilmActions.fetchAllForEventBucket.defer(eventBucketId);
        }
        if (types.indexOf("passes") !== -1) {
          PassActions.fetchAllForEventBucket.defer(eventBucketId);
        }
        if (types.indexOf("event_buckets") !== -1) {
          EventBucketActions.fetchAll.defer();
        }
      }

      let {
          getVenuesForEventBucket,
          getPassBucketsForEventBucket,
          getAllPassBuckets,
          getEventsForEventBucket,
          getTagsForEventBucket,
          getCouponsForEventBucket,
          getItemBucketsForEventBucket,
          getFilmsForEventBucket,
          getPassesForEventBucket,
          getEventBuckets
        } = newProps,
        objects = {};

      if (types.indexOf("venues") !== -1) {
        objects.venues = getVenuesForEventBucket(eventBucketId);
      }
      if (types.indexOf("pass_buckets") !== -1) {
        objects.pass_buckets = getPassBucketsForEventBucket(eventBucketId);
      }
      if (types.indexOf("all_pass_buckets") !== -1) {
        objects.all_pass_buckets = getAllPassBuckets();
      }
      if (types.indexOf("events") !== -1) {
        objects.events = getEventsForEventBucket(eventBucketId);
      }
      if (types.indexOf("ticket_buckets") !== -1) {
        let event =
            newProps.eventId && EventStore.getEventById(newProps.eventId),
          events = newProps.eventId
            ? event
              ? [event]
              : []
            : getEventsForEventBucket(eventBucketId);

        objects.ticket_buckets = [].concat.apply(
          [],
          events.map(event => {
            return event.ticket_buckets.map(b => {
              return Object.assign({}, b, { event: event });
            });
          })
        );
      }
      if (types.indexOf("ticket_bucket.variants") !== -1) {
        objects["item_bucket.variants"] = (
          EventStore.getTicketBucketWithEventById(newProps.ticketBucketId) || {
            variants: []
          }
        ).variants;
      }
      if (types.indexOf("tags") !== -1) {
        objects.tags = getTagsForEventBucket(eventBucketId);
      }
      if (types.indexOf("visibleTags") !== -1) {
        objects.visibleTags = getTagsForEventBucket(eventBucketId, true);
      }
      if (types.indexOf("coupons") !== -1) {
        objects.coupons = getCouponsForEventBucket(eventBucketId);
      }
      if (types.indexOf("item_buckets") !== -1) {
        objects.item_buckets = getItemBucketsForEventBucket(eventBucketId);
      }
      if (types.indexOf("item_bucket.variants") !== -1) {
        objects["item_bucket.variants"] = (
          ItemBucketStore.getItemBucketById(newProps.itemBucketId) || {
            variants: []
          }
        ).variants;
      }
      if (types.indexOf("films") !== -1) {
        objects.films = getFilmsForEventBucket(eventBucketId);
      }
      if (types.indexOf("passes") !== -1) {
        objects.passes = getPassesForEventBucket(eventBucketId);
      }
      if (types.indexOf("event_buckets") !== -1) {
        objects.event_buckets = getEventBuckets();
      }

      return objects;
    };

    if (typeof eventBucketId === "string") {
      eventBucketId = [eventBucketId];
    }
    let objects = {};
    for (const id of eventBucketId) {
      const bucketObjects = load(id);
      for (const k in bucketObjects) {
        objects[k] = (objects[k] || []).concat(bucketObjects[k]);
      }
    }

    const locationData = SystemStatesStore.getLocationData();
    if (types.indexOf("continents") !== -1) {
      objects.continents = locationData.continents.map(({ name, code }) => ({
        name,
        id: code
      }));
    }
    if (types.indexOf("countries") !== -1) {
      objects.countries = locationData.countries.map(({ name, code }) => ({
        name,
        id: code
      }));
    }
    if (types.indexOf("regions") !== -1) {
      objects.regions = locationData.regions.map(({ name, code }) => ({
        name,
        id: code
      }));
    }

    this.setState({
      objects,
      fetchedEventBuckets: this.state.fetchedEventBuckets.concat(eventBucketId)
    });

    if ((newProps.focus || newProps.autoFocus) && this.select) {
      setTimeout(() => this.select && this.select.focus(), 0);
    }

    setTimeout(() => {
      this.setState({ hasMounted: true });
    }, 0);
  }

  render() {
    let {
        autoFocus,
        focus,
        style,
        label,
        creatable,
        creatablePromptText,
        creatableShowNewOptionAtTop,
        onCreateSelect,
        labelFormatter,
        isDisabledFunction,
        filterBy,
        multi,
        noResultsText,
        placeholderText,
        value,
        changeValueFunction,
        sortFunction,
        injectOptions,
        onChange,
        sortDisabledToEnd,
        labelToFilterString,
        isFetching,
        ...rest
      } = this.props,
      { objects, isReordering, reorderingHeight, hasMounted } = this.state;

    let allOptions = this.getOptions();

    if (sortFunction) {
      allOptions.sort(sortFunction);
    }

    if (sortDisabledToEnd) {
      allOptions = allOptions
        .filter(o => !o.disabled)
        .concat(allOptions.filter(o => o.disabled));
    }

    if (UserStore.isGod() && !isFetching && hasMounted) {
      // Include any old deleted options too. This way the god user can have easy visibility
      // and delete them if needed.
      let currentlySelected = value
        ? Array.isArray(value)
          ? value
          : [value]
        : [];
      let deletedValues = currentlySelected.filter(
        o => !allOptions.find(a => a.value === o)
      );
      allOptions = allOptions.concat(
        deletedValues.map((v, i) => ({
          value: v,
          label: `DELETED (${v})`,
          isDeleted: true,
          rawValue: {},
          filterableLabel: ""
        }))
      );
    }

    const SelectComponent = creatable ? Creatable : Select;
    return (
      <div style={style}>
        {label ? (
          <div
            style={{
              fontSize: 12,
              color: "rgba(33, 33, 33, 0.498039)",
              marginBottom: 5,
              marginTop: 10,
              display: "flex"
            }}
          >
            <span style={{ flex: 1 }}>{label}</span>
            {isReordering ||
            (multi &&
              Array.isArray(value) &&
              value.filter(v => allOptions.find(o => o.value === v)).length >=
                2) ? (
              <span>
                {isReordering ? (
                  <span style={{ marginRight: 5 }}>Drag to re-order</span>
                ) : (
                  ""
                )}
                {!this.props.disabled ? (
                  <InlineButton
                    label={
                      isReordering ? (
                        "Finish re-ordering"
                      ) : (
                        <span style={{ position: "relative", paddingLeft: 14 }}>
                          <svg
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            width={12}
                            height={12}
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 1
                            }}
                          >
                            <path d="M0 0h24v24H0z" fill="none" />
                            <path d="M3 15h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V9H3v2zm0-6v2h18V5H3z" />
                          </svg>
                          Adjust order
                        </span>
                      )
                    }
                    onClick={() => {
                      this.setState({ isReordering: !isReordering });
                    }}
                  />
                ) : null}
              </span>
            ) : null}
          </div>
        ) : null}

        <DragDropContext
          onDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
        >
          <div
            style={{
              position: "relative",
              height: isReordering ? reorderingHeight : null
            }}
          >
            <Droppable droppableId="select-container" direction="vertical">
              {provided => (
                <div
                  className={isReordering ? "droppable" : "droppable-inactive"}
                  style={isReordering ? { height: "100%" } : undefined}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <SelectComponent
                    autosize={true}
                    multi={multi}
                    openOnFocus={true}
                    promptTextCreator={creatablePromptText}
                    onNewOptionClick={onCreateSelect}
                    showNewOptionAtTop={creatableShowNewOptionAtTop}
                    placeholder={placeholderText || "None selected"}
                    noResultsText={noResultsText || "No results found"}
                    value={multi && !Array.isArray(value) ? [value] : value}
                    options={allOptions}
                    clearable={isReordering ? false : undefined}
                    menuRenderer={
                      isReordering
                        ? () => {}
                        : props => {
                            const options = props.options.slice(0, /*50*/ 500);
                            return defaultMenuRenderer({
                              ...props,
                              options
                            });
                          }
                    }
                    filterOption={
                      labelToFilterString
                        ? (o, f) =>
                            o.filterableLabel.indexOf(
                              f.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()
                            ) !== -1
                        : undefined
                    }
                    ref={r => {
                      this.select = r;
                    }}
                    onChange={this.onValueChange}
                    valueComponent={isReordering ? this.renderValue : undefined}
                    {...rest}
                  />
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      </div>
    );
  }
}

export default connectToStores(InjectedSelect);



// WEBPACK FOOTER //
// ./src/app/components/partials/injected-select.jsx